import Layout from "components/structure/Layout";
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  TextField,
  CircularProgress,
  InputAdornment,
  Grid,
  FormControl,
  MenuItem,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Button,
} from "@mui/material";
import iconSearchs from "../../../assets/iconSearchs.svg";
import "./PromptActionNotification.css";
import { SnackbarContext } from "context/snackBarContext";
import NoData from "assets/noData.svg";
import { useTranslation } from "react-i18next";
import CheckboxTick from "assets/CheckboxTick.svg";
import CheckboxUntick from "assets/CheckboxUntick.svg";
import SaveConfirmModal from "./SaveConfirmModal";
import { useNavigate } from "react-router-dom";
import { useBlocker } from "hooks/useBlocker";
import _ from "lodash";
import PromptActionService from "services/PromptActionService";
import SeasonRegistrationService from "services/SeasonRegistrationService";
import { AuthData } from "auth/AuthWarpper";

let staticsetSelected = "";

const PromptActionNotification = () => {
  const { t } = useTranslation();
  const {user}= AuthData()
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [originalUserData, setOriginalUserData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const { setOpenAlert, setSeverity, setMessage } = useContext(SnackbarContext);
  const [saveConfirmModal, setSaveConfirmModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [moreAttributes, setMoreAttributes] = useState([]);
  const [selected, setSelected] = useState([]);
  const [profileNames, setProfileNames] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cropYears, setCropYears] = useState([]);
  const [selectedCropYear, setSelectedCropYear] = useState("");

  const moduleName = "Manage Operations";
  const functionalityName = "Prompt Action Notification";

  const read_only_user = user?.accessibleModules
    ?.find(module => module.module_name === moduleName)
    ?.accessible_functionalities
    ?.find(func => func.functionality_name === functionalityName)
    ?.read_only;

  const FetchAllRoles = () => {
    PromptActionService.getAllRoles()
      .then((res) => {
        let response = res?.data?.response;
        const excludedRoles = ["Field Technician", "FT Plantix"];
        const roles = [
          ...new Set(
            response
              ?.map((ele) => ele?.profileName)
              ?.filter((ele) =>!excludedRoles.includes(ele))
          ),
        ]?.map((ele, index) => ({
          id: index + 1,
          name: ele,
        }));
        setMoreAttributes(roles);
        setSelected(roles);
        setProfileNames(roles.map((ele) => ele.name));
      })
      .catch((error) => {
        setMoreAttributes([]);
        setSelected([]);
        setProfileNames([]);
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
        setSeverity("error");
      });
  };

  const callPromptActionUserAPI = (keyword, profileNames, cropYear = selectedCropYear) => {
    setLoading(true);
    const profileList = profileNames.join(",");
    PromptActionService.getPromptActionUsers(keyword, profileList, cropYear)
      .then((res) => {
        let resp = res?.data?.response;
        setUserData(
          resp.map((ele) => ({
            ...ele,
            isChecked:
              ele?.hierarchyLevelOrder === 1 || ele?.hierarchyLevelOrder === 2
                ? true
                : !!ele?.isChecked,
          }))
        );
        setOriginalUserData(
          resp.map((ele) => ({
            ...ele,
            isChecked: !!ele?.isChecked,
          }))
        );
        setLoading(false);
      })
      .catch((error) => {
        setUserData([]);
        setOriginalUserData([]);
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
        setSeverity("error");
        setLoading(false);
      });
  };

  const updatePromptActionUser = (payload) => {
    setSaveLoading(true);
    PromptActionService.updatePromptActionUsers(payload, selectedCropYear)
      .then((res) => {
        if (res.status === 200) {
          setMessage("Saved Successfully");
          setOpenAlert(true);
          setSeverity("success");
          setSaveLoading(false);
          setOriginalUserData([...userData]);
          setHasUnsavedChanges(false);
        }
      })
      .catch((error) => {
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
        setSeverity("error");
        setSaveLoading(false);
      });
  };

  useEffect(() => {
    FetchAllRoles();
    SeasonRegistrationService.getCropYears()
      .then((response) => {
        const cropYearsFromApi = (response?.data?.response || []).map(
          (item) => item.cropYear
        );
        setCropYears(cropYearsFromApi);
        if (cropYearsFromApi.length > 0) {
          setSelectedCropYear(Math.max(...cropYearsFromApi));
        }
      })
      .catch((error) => {
        setMessage(error?.response?.data?.message || error?.message);
        setOpenAlert(true);
        setSeverity("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialRender = useRef(true);

  useEffect(() => {
    if (!selectedCropYear) return;
    if (initialRender.current && profileNames.length > 0) {
      callPromptActionUserAPI(keyword, profileNames, selectedCropYear);
      initialRender.current = false;
    } else if (!initialRender.current) {
      callPromptActionUserAPI(keyword, profileNames, selectedCropYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileNames, selectedCropYear]);

  useEffect(() => {
    setProfileNames(selected.map((ele) => ele.name));
  }, [selected]);

  useEffect(() => {
    const filteredUserData = userData?.filter(
      (ele) => ![1, 2].includes(ele?.hierarchyLevelOrder)
    );
    const filteredOriginalData = originalUserData?.filter(
      (ele) => ![1, 2].includes(ele?.hierarchyLevelOrder)
    );
    setHasUnsavedChanges(!_.isEqual(filteredUserData, filteredOriginalData));
  }, [userData]);

  const blocker = useBlocker(({ action, location: nextLocation }) => {
    let navigateparam = nextLocation.pathname;

    if (/\/Agri360(\/v1)?\/?$/.test(navigateparam)) {
      // Ends exactly with /Agri360 or /Agri360/v1
      navigateparam = "/";
    } else if (navigateparam.includes("/Agri360/v1")) {
      // Remove everything up to and including /Agri360/v1
      navigateparam = navigateparam.replace(/^.*\/Agri360\/v1/, "");
    } else if (navigateparam.includes("/Agri360")) {
      // Remove everything up to and including /Agri360
      navigateparam = navigateparam.replace(/^.*\/Agri360/, "");
    }
    if (hasUnsavedChanges) {
      setPendingNavigation(navigateparam);
      setSaveConfirmModal(true);
      return true; // Block navigation
    }
    return false; // Allow navigation
  }, hasUnsavedChanges);

  const handleInputChange = (e, index) => {
    const { checked } = e.target;

    setUserData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, isChecked: checked } : row))
    );
  };

  const saveForm = () => {
    const modifiedUserData = userData
      .filter((ele, index) => {
        const original = originalUserData[index];
        return (
          ele.userId === original.userId && ele.isChecked !== original.isChecked
        );
      })
      .map((ele) => ({
        userId: ele.userId,
        isChecked: ele.isChecked,
      }));
    if (modifiedUserData.length === 0) {
      setHasUnsavedChanges(false);
      return;
    }
    updatePromptActionUser(modifiedUserData);
  };

  const handleSaveAndExit = () => {
    saveForm();
    setSaveConfirmModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    } else {
      navigate(-1);
    }
    setPendingNavigation(null);
    blocker.reset();
  };

  const handleExitWithoutSave = () => {
    setSaveConfirmModal(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    } else {
      navigate(-1);
    }
    setPendingNavigation(null);
    blocker.reset();
  };

  const handleCancelExit = () => {
    setSaveConfirmModal(false);
    blocker.reset();
    setPendingNavigation(null);
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        const message =
          "You have unsaved changes. Are you sure you want to leave?";
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [userData, originalUserData]);

  let isAllSelected =
    moreAttributes.length > 0 && selected.length === moreAttributes.length;

  const handleChangeAttributes = (event) => {
    const option = event.target.value;
    if (option[option.length - 1] === "all") {
      setSelected(
        selected.length === moreAttributes.length ? [] : moreAttributes
      );
      staticsetSelected =
        selected.length === moreAttributes.length ? [] : moreAttributes;
      return;
    }
    let updatedOptions = removeAllDuplicates(option);
    setSelected(updatedOptions);
    staticsetSelected = updatedOptions;
  };

  const handleCropYearChange = (e) => {
    setSelectedCropYear(e.target.value);
  };

  const handleGetButton = () => {
    callPromptActionUserAPI(keyword, profileNames, selectedCropYear);
  };

  const removeAllDuplicates = (option) => {
    const occurrences = {};
    option?.forEach((item) => {
      const key = `${item.id}-${item.name}`;
      occurrences[key] = (occurrences[key] || 0) + 1;
    });
    const uniqueData = option.filter(
      (item) => occurrences[`${item.id}-${item.name}`] === 1
    );
    return uniqueData;
  };

  return (
    <Layout title={"Prompt Action Notification"}>
      <Box sx={{ paddingBottom: "140px" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontWeight: 600,
              fontSize: "19px",
              color: "rgba(115, 125, 134, 1)",
              marginTop: "-8px",
            }}
          >
            Prompt Action Notification
          </Box>
        </Box>
        <Box
          sx={{
            paddingTop: "10px",
            pl: "8px",
            display: "flex",
            gap: 4,
            flexDirection: { xs: "column", sm: "row" },
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              sx={{ marginLeft: "-8px", display: "flex", alignItems: "center" }}
            >
              <FormControl sx={{ m: 1, minWidth: { xs: 150, md: 200 } }}>
                <TextField
                  select
                  value={selectedCropYear}
                  onChange={handleCropYearChange}
                  displayEmpty
                  label={selectedCropYear ? "" : "Crop Year"}
                  inputProps={{ "aria-label": "Crop Year" }}
                  size="small"
                  sx={{
                    "& .MuiInputBase-root": { height: "40px" },
                    "& .MuiSelect-select": {
                      paddingTop: "8px",
                      paddingBottom: "8px",
                    },
                  }}
                >
                  {cropYears.map((cropYear) => (
                    <MenuItem key={cropYear} value={cropYear}>
                      {cropYear}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>
              <Box sx={{ width: "28%", paddingLeft: "0px" }}>
                <TextField
                  fullWidth
                  className="user-search-input"
                  placeholder={t("search")}
                  variant="outlined"
                  onChange={(event) => {
                    setKeyword(event.target.value);
                  }}
                  value={keyword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <img src={iconSearchs} />
                      </InputAdornment>
                    ),
                    sx: {
                      height: "40px",
                      borderRadius: "10px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none !important", // This removes the border
                      },
                    },
                  }}
                />
              </Box>
              <FormControl sx={{ m: 1, width: "18%" }}>
                <TextField
                  select
                  label={t("promptActionNotification.roles")} // This will be displayed as the label
                  placeholder="" // Placeholder text
                  value={selected}
                  onChange={handleChangeAttributes}
                  size="small" // makes it more compact
                  sx={{
                    height: "40px",
                    "& .MuiInputBase-root": {
                      height: "40px",
                      padding: "4px 8px",
                    },
                    "& .MuiInputBase-input": {
                      padding: "4px 0px",
                      fontSize: "14px",
                    },
                  }}
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) =>
                      selected.map((select) => select.name).join(", "),
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 250,
                          overflowY: "auto",
                        },
                      },
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* Add any adornment if needed */}
                      </InputAdornment>
                    ),
                  }}
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={
                          selected.length > 0 &&
                          selected.length < moreAttributes.length
                        }
                        icon={<img src={CheckboxUntick} alt="Unchecked" />}
                        checkedIcon={<img src={CheckboxTick} alt="Checked" />}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Select All" />
                  </MenuItem>
                  {moreAttributes &&
                    moreAttributes.map((option) => (
                      <MenuItem key={option?.id} value={option}>
                        <ListItemIcon>
                          <Checkbox
                            checked={
                              selected.find((ele) => ele?.id === option?.id) !==
                              undefined
                            }
                            icon={<img src={CheckboxUntick} alt="Unchecked" />}
                            checkedIcon={
                              <img src={CheckboxTick} alt="Checked" />
                            }
                          />
                        </ListItemIcon>
                        <ListItemText primary={option?.name} />
                      </MenuItem>
                    ))}
                </TextField>
              </FormControl>
              <Button
                sx={{
                  borderRadius: "10px",
                  width: "87px",
                  height: "40px",
                  background: "#102A62",
                  marginLeft: "10px",
                }}
                variant="contained"
                onClick={handleGetButton}
                disabled={loading || !selectedCropYear}
              >
                {loading && <CircularProgress size={20} />}
                {t("promptActionNotification.get")}
              </Button>
            </Grid>
          </Grid>
        </Box>
        {userData?.length > 0 && (
          <>
            <div className="PAnotification-table-wrapper">
              {/* Fixed Header Table */}
              <table className="PAnotification-table header-table">
                <colgroup>
                  <col style={{ width: "70px" }} />
                  <col />
                  <col style={{ width: "25%" }} />
                  <col />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th></th>
                    <th>{t("promptActionNotification.oneId")}</th>
                    <th>{t("promptActionNotification.name")}</th>
                    <th>{t("promptActionNotification.emailId")}</th>
                    <th>{t("role")}</th>
                  </tr>
                </thead>
              </table>

              {/* Scrollable Body Table */}
              <div className="PAnotification-table-body-scroll">
                <table className="PAnotification-table body-table">
                  <colgroup>
                    <col style={{ width: "70px" }} />
                    <col />
                    <col style={{ width: "25%" }} />
                    <col />
                    <col />
                  </colgroup>
                  <tbody>
                    {userData.map((row, index) => (
                      <tr key={index} className="PAnotification-table-row">
                        <td style={{ textAlign: "right" }}>
                          <Checkbox
                            disabled={
                              read_only_user||
                            row?.hierarchyLevelOrder === 1 ||
                              row?.hierarchyLevelOrder === 2
                            }
                            checked={row?.isChecked || false}
                            onChange={(e) => handleInputChange(e, index)}
                            sx={{
                              '&.Mui-disabled.Mui-checked': {
                                              color: '#112A63',
                                              opacity:0.6
                                                }
                            }}
                          />
                        </td>
                        <td>{row?.batOneId}</td>
                        <td>{row?.name}</td>
                        <td>{row?.email}</td>
                        <td>{row?.profileName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <Grid
              container
              sx={{
                position: "fixed",
                bottom: 56,
                width: "100%",
                justifyContent: "center",
                gap: "23px",
                padding: "16px 0",
                backgroundColor: "#f5f5f7",
                zIndex: 1000,
              }}
            >
              <Button
                disabled={loading}
                style={{ backgroundColor: "#737D86", minWidth: "92px" }}
                variant="contained"
                onClick={() =>
                  hasUnsavedChanges ? setSaveConfirmModal(true) : navigate(-1)
                }
              >
                {t("back")}
              </Button>
              <Button
                disabled={read_only_user||saveLoading || !hasUnsavedChanges}
                style={{
                  backgroundColor: "#EF7D00",
                  minWidth: "92px",
                  color: "white",
                  opacity: saveLoading || !hasUnsavedChanges ? 0.3 : 1,
                }}
                sx={{
                  '&.Mui-disabled': {
                        backgroundColor: "#EF7D00", 
                        color: "#ffffff",    
                        opacity: 0.5,          
                        },
                }}
                variant="contained"
                type="button"
                onClick={saveForm}
              >
                {saveLoading && <CircularProgress size={20} />}
                {t("promptActionNotification.save")}
              </Button>
            </Grid>
          </>
        )}
        {userData?.length == 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: "column",
              alignItems: "center",
              marginTop: "40px",
            }}
          >
            <img src={NoData} style={{ height: "100px" }} alt="No Data" />
            <p>{t("promptActionNotification.nodata")}</p>
          </Box>
        )}
      </Box>
      <SaveConfirmModal
        open={saveConfirmModal}
        onClose={handleCancelExit}
        onLeaveChanges={handleExitWithoutSave}
        onConfirm={handleSaveAndExit}
      />
    </Layout>
  );
};

export default PromptActionNotification;
